import "video.js/dist/video-js.css"
import "videojs-hls-quality-selector/src/plugin"

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react"
import videojs from "video.js"
import Player from "video.js/dist/types/player"

import { getMimetype } from "./utils"
import styles from "./VideoPlayerV2.module.css"

const MenuItem = videojs.getComponent("MenuItem")
const MenuButton = videojs.getComponent("MenuButton")

// videojs.registerComponent("LanguageMenuButton", LanguageMenuButton)

// Referred from: https://github.com/videojs/video.js/issues/8151
class CustomMenuButton extends MenuButton {
  createItems() {
    const items: any[] = []
    const { myItems } = this.options_

    if (!Array.isArray(myItems)) return items

    myItems.forEach((itemData: any) => {
      const menuItem = new MenuItem(this.player(), itemData) as any

      menuItem.handleClick = function () {
        // Unselect others
        const button = this.player().controlBar.getChild(
          "CustomMenuButton"
        ) as any
        button.items.forEach((i: any) => i.selected(false))

        // Select current
        this.selected(true)

        itemData.clickHandler?.()
      }

      items.push(menuItem)
    })

    return items
  }

  buildCSSClass() {
    return `${super.buildCSSClass()}`
  }
}

export type VideoProps = {
  /**
   * List of available video sources/multiple languages.
   */
  sources: {
    language: string
    uuid: string
    localeCode?: string
    url: string
  }[]
  /**
   * Current video language.
   */
  defaultLanguageId: string
  /**
   * Callback for when the video language changes.
   */
  onLanguageChange?: (language: string) => void
  /**
   * Callback for when the subtitles toggled changes.
   */
  onSubtitleChange?: (isEnabled: boolean) => void
  /**
   * Whether to autoplay the video
   */
  autoPlay?: boolean
  /**
   * Styles applied to the video player.
   */
  style?: React.CSSProperties
  /**
   * Video thumbnail
   */
  poster?: string

  /**
   * Video thumbnail
   */
  showSubtitles?: boolean
}

export type VideoPlayerHandle = {
  currentTime: number
  paused: boolean
  playbackRate: number
  duration: number
  play: () => void
  pause: () => void
  setPoster: (posterUrl: string) => void
  addEventListener: (event: string, cb: any) => void
  removeEventListener: (event: string, cb: any) => void
}

const VideoPlayerV2 = forwardRef<VideoPlayerHandle, VideoProps>(
  (props, ref) => {
    const playerRef = useRef<Player | null>(null)
    const videoContainer = useRef<HTMLDivElement>(null)
    const [currentLangId, setCurrentLangId] = useState<string>(
      props.defaultLanguageId
    )
    const [showSubtitles, setShowSubtitles] = useState(false)

    useImperativeHandle(ref, () => ({
      get currentTime() {
        return playerRef.current?.currentTime() ?? 0
      },
      set currentTime(time: number) {
        playerRef.current?.currentTime(time)
      },

      get paused() {
        return playerRef.current?.paused() ?? true
      },

      get playbackRate() {
        return playerRef.current?.playbackRate() ?? 1
      },

      get duration() {
        return playerRef.current?.duration() ?? 0
      },

      set playbackRate(rate: number) {
        playerRef.current?.playbackRate(rate)
      },

      play() {
        playerRef.current?.play()
      },

      setPoster(posterUrl: string) {
        if (playerRef.current) {
          playerRef.current.poster(posterUrl)
        }
      },

      pause() {
        playerRef.current?.pause()
      },

      addEventListener(event, cb) {
        playerRef.current?.on(event, cb)
      },

      removeEventListener(event, cb) {
        playerRef.current?.off(event, cb)
      },
    }))

    const changeLanguage = (id: string) => {
      const player = playerRef.current
      if (!player) return

      const lang = props.sources.find(l => l.uuid === id)

      // Switch video
      player.src({ src: lang?.url, type: getMimetype(lang?.url) })
      toggleSubtitles(showSubtitles)

      setCurrentLangId(id)
      if (props.onLanguageChange) props.onLanguageChange(id)
    }

    const toggleSubtitles = useCallback((isEnabled?: boolean) => {
      const player = playerRef.current
      if (!player) return

      setShowSubtitles(prev => {
        const newValue = isEnabled !== undefined ? isEnabled : !prev

        const tracks = player.textTracks() as any

        if (tracks) {
          for (let i = 0; i < tracks.length; i++) {
            const track = tracks[i]

            if (track.kind === "subtitles" || track.kind === "captions") {
              track.mode = newValue ? "showing" : "hidden"
            }
          }
        }

        if (props.onSubtitleChange) props.onSubtitleChange(newValue)

        return newValue
      })
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Build video options
    const buildVideoOptions = () => {
      const selectedLang = props.sources.find(l => l.uuid === currentLangId)

      return {
        userActions: {
          // video.js's default handler leaves the fullscreen promise unhandled. Double taps
          // synthesized by drag-drop-touch aren't user gestures, so browsers reject them.
          doubleClick() {
            const player = this as unknown as Player
            if (player.isFullscreen())
              Promise.resolve(player.exitFullscreen()).catch(() => {})
            else Promise.resolve(player.requestFullscreen()).catch(() => {})
          },
          hotkeys(event: KeyboardEvent) {
            const player = this as unknown as Player

            switch (event.key) {
              case "m":
                player.muted(!player.muted())
                break
              case "f":
                if (player.isFullscreen()) player.exitFullscreen()
                else player.requestFullscreen()
                break
              case "p":
              case " ":
                event.preventDefault()
                if (player.paused()) player.play()
                else player.pause()
                break
              case "ArrowUp":
                player.volume(Math.min(1, (player.volume() || 0) + 0.1))
                break
              case "ArrowDown":
                player.volume(Math.max(0, (player.volume() || 0) - 0.1))
                break
              case "ArrowRight":
                player.currentTime((player.currentTime() || 0) + 5)
                break
              case "ArrowLeft":
                player.currentTime((player.currentTime() || 0) - 5)
                break
              default:
                break
            }
          },
        },
        controls: true,
        autoplay: props.autoPlay,
        preload: "metadata",
        fluid: true,
        responsive: true,
        playsinline: true,
        width: "100%",
        poster:
          props?.poster ??
          "https://assets.suraasa.com/lms/default-video-player-thumbnail.png",
        sources: [
          {
            src: selectedLang?.url,
            type: getMimetype(selectedLang?.url),
          },
        ],
        controlBar: {
          subsCapsButton: false,
        },
        playbackRates: [0.5, 0.75, 1, 1.25, 1.5, 2],
        html5: {
          vhs: {
            enableLowInitialPlaylist: true,
          },
        },
      }
    }

    // Initialize player effect
    useEffect(() => {
      // Only initialize if we don't have a player and container exists
      if (!playerRef.current) {
        const videoElement = document.createElement("video-js")
        if (!videoContainer.current) return

        videoElement.classList.add("vjs-big-play-centered")
        videoContainer.current.appendChild(videoElement)

        try {
          // Initialize Video.js player
          const player: any = videojs(videoElement, buildVideoOptions(), () => {
            // Initialize quality selector plugin
            if (player.hlsQualitySelector) {
              player.hlsQualitySelector({
                displayCurrentQuality: true,
              })
            }

            videojs.registerComponent("CustomMenuButton", CustomMenuButton)
            const children = player.controlBar.children()
            const quality = player.controlBar.getChild("HlsQualitySelector")
            const baseIndex = quality
              ? children.indexOf(quality)
              : children.length

            const subtitlesButton = player.controlBar.addChild(
              "button",
              {},
              baseIndex - 2
            )

            subtitlesButton.addClass("vjs-subs-caps-button")
            subtitlesButton.addClass(styles.vjsSubsOff)

            subtitlesButton.el().onclick = function () {
              toggleSubtitles()
              this.blur()
            }

            const tracks = player.textTracks()

            const updateSubtitlesUI = () => {
              if (!tracks) return

              let enabled = false

              for (let i = 0; i < tracks.length; i++) {
                if (
                  (tracks[i].kind === "subtitles" ||
                    tracks[i].kind === "captions") &&
                  tracks[i].mode === "showing"
                ) {
                  enabled = true
                  break
                }
              }

              subtitlesButton.toggleClass(styles.vjsSubsOn, enabled)
              subtitlesButton.toggleClass(styles.vjsSubsOff, !enabled)
            }

            if (tracks) {
              tracks.addEventListener("change", updateSubtitlesUI)
            }

            if (props.sources.length > 1) {
              player.controlBar.addChild(
                "CustomMenuButton",
                {
                  title: "Languages",
                  className: "vjs-chapters-button",
                  myItems: props.sources.map(lang => ({
                    label: lang.language,
                    selectable: true,
                    selected: lang.uuid === currentLangId,
                    clickHandler() {
                      changeLanguage(lang.uuid)
                    },
                  })),
                },
                baseIndex - 1
              )
            }
          })

          // Store player reference
          playerRef.current = player
        } catch (error) {
          console.error("VideoJS: Failed to initialize player", error)
        }
      }
      // Update existing player if options changed
      else if (playerRef.current) {
        const player = playerRef.current
        const newOptions = buildVideoOptions()

        try {
          // Update source if changed
          const currentSrc = player.currentSrc()
          const newSrc = newOptions.sources[0].src

          if (newSrc && newSrc !== currentSrc) {
            player.src(newOptions.sources)
          }

          // Update other properties
          player.autoplay(newOptions.autoplay)
          player.poster(newOptions.poster)
        } catch (error) {
          console.error("VideoJS: Error updating player", error)
        }
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
      if (!playerRef.current) return

      const player = playerRef.current

      player.on("loadedmetadata", () => {
        if (props.showSubtitles) {
          toggleSubtitles(true)
        }
      })
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.showSubtitles])

    // Cleanup effect
    useEffect(() => {
      return () => {
        if (playerRef.current && !playerRef.current.isDisposed()) {
          try {
            playerRef.current.dispose()
          } catch (error) {
            console.error("VideoJS: Error during cleanup", error)
          } finally {
            playerRef.current = null
          }
        }
      }
    }, [])

    return (
      <>
        <div
          ref={videoContainer}
          // eslint-disable-next-line tailwindcss/no-custom-classname
          className="video-container"
          style={{
            ...props.style,
          }}
        />
      </>
    )
  }
)
VideoPlayerV2.displayName = "VideoPlayerV2"
export default VideoPlayerV2
